import * as React from "react"

interface InfiniteScrollTriggerProps {
  /**
   * Function called when more data should be fetched
   */
  onFetchNext: () => void

  /**
   * Whether currently fetching more data
   */
  isFetching: boolean

  /**
   * Whether there's more data available to fetch
   */
  hasNextPage: boolean

  /**
   * How many pixels from bottom to trigger fetch (default: 500)
   */
  triggerOffset?: number

  /**
   * Custom loading content (optional)
   */
  loadingContent?: React.ReactNode

  /**
   * Custom "no more data" content (optional)
   */
  endContent?: React.ReactNode

  /**
   * Container element ref for scroll detection
   * If not provided, will use window scroll
   */
  containerRef?: React.RefObject<HTMLElement>
}

export function InfiniteScrollTrigger({
  onFetchNext,
  isFetching,
  hasNextPage,
  triggerOffset = 250,
  loadingContent,
  endContent,
  containerRef,
}: InfiniteScrollTriggerProps) {
  const triggerRef = React.useRef<HTMLDivElement>(null)

  // Function to check if we should fetch more data
  const checkIfShouldFetch = React.useCallback(
    (element?: HTMLElement | null) => {
      const container = element || containerRef?.current

      if (!container) {
        // Fallback to window scroll
        const { scrollHeight, scrollTop, clientHeight } = document.documentElement
        if (scrollHeight - scrollTop - clientHeight < triggerOffset && !isFetching && hasNextPage) {
          onFetchNext()
        }
        return
      }

      const { scrollHeight, scrollTop, clientHeight } = container
      if (scrollHeight - scrollTop - clientHeight < triggerOffset && !isFetching && hasNextPage) {
        onFetchNext()
      }
    },
    [onFetchNext, isFetching, hasNextPage, triggerOffset, containerRef]
  )

  // Check on mount and after fetch state changes
  React.useEffect(() => {
    checkIfShouldFetch(containerRef?.current)
  }, [checkIfShouldFetch, containerRef])

  // Set up scroll listener
  React.useEffect(() => {
    const container = containerRef?.current
    const scrollElement = container || window

    const handleScroll = (e: Event) => {
      const target = container || (e.target as Document)?.documentElement
      checkIfShouldFetch(target as HTMLElement)
    }

    scrollElement.addEventListener("scroll", handleScroll, { passive: true })

    return () => {
      scrollElement.removeEventListener("scroll", handleScroll)
    }
  }, [checkIfShouldFetch, containerRef])

  // Intersection Observer fallback for better performance
  React.useEffect(() => {
    if (!triggerRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetching) {
          onFetchNext()
        }
      },
      {
        root: containerRef?.current || null,
        rootMargin: `${triggerOffset}px`,
        threshold: 0,
      }
    )

    observer.observe(triggerRef.current)

    return () => observer.disconnect()
  }, [onFetchNext, hasNextPage, isFetching, triggerOffset, containerRef])

  return (
    <div ref={triggerRef} className="infinite-scroll-trigger w-full">
      {isFetching && (
        <div className="flex items-center justify-center py-6 bg-muted/30">
          {loadingContent || (
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
              <span className="text-sm font-medium text-muted-foreground">
                {/* TODO: will be switched with actual loading UI */}
                Loading more rows...
              </span>
            </div>
          )}
        </div>
      )}

      {/* TODO: Add this back in with custom text prop */}
      {!hasNextPage && !isFetching && endContent && (
        <div className="flex items-center justify-center py-4 bg-muted/20">{endContent}</div>
      )}

      {/* Hidden trigger area for intersection observer - ensures we don't interfere with existing data */}
      {hasNextPage && !isFetching && (
        <div className="h-px w-full opacity-0 pointer-events-none" aria-hidden="true" />
      )}
    </div>
  )
}
